import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../../services/api';

// Helper: tu backend a veces manda el error como { message: "..." } (la
// mayoría de tus controllers) y a veces como { msg: "..." } (login usa este
// otro nombre) - revisamos ambos para nunca perder el mensaje real y caer
// en el genérico de axios ("Request failed with status code 400").
const extractErrorMessage = (error) =>
  error.response?.data?.message || error.response?.data?.msg || error.message;

const initialState = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  token: localStorage.getItem('token') || null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
  // Set after sign-up, or when login is refused because the email isn't
  // confirmed yet - the forms use it to show the "check your email" state.
  pendingVerificationEmail: null,
};

export const login = createAsyncThunk('auth/login', async ({ email, password }, thunkAPI) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/login`, { email, password });
    if (response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.userFound));
      localStorage.setItem('token', response.data.token);
      return { user: response.data.userFound, token: response.data.token };
    }
    return thunkAPI.rejectWithValue('No token received');
  } catch (error) {
    if (error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
      return thunkAPI.rejectWithValue({ message: extractErrorMessage(error), unverifiedEmail: email });
    }
    return thunkAPI.rejectWithValue(extractErrorMessage(error));
  }
});

// Sign-up doesn't log the user in: the API emails a confirmation link and
// the account can sign in once it's confirmed.
export const register = createAsyncThunk('auth/register', async ({ fullname, email, password, confirmPassword, lang }, thunkAPI) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/register`, { fullname, email, password, confirmPassword, lang });
    return { email: response.data.email };
  } catch (error) {
    return thunkAPI.rejectWithValue(extractErrorMessage(error));
  }
});

// Called by the /verify-email page with the token from the email link.
// On success the API returns a session, same as login.
export const verifyEmail = createAsyncThunk('auth/verifyEmail', async (token, thunkAPI) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/verify-email`, { token });
    localStorage.setItem('user', JSON.stringify(response.data.userFound));
    localStorage.setItem('token', response.data.token);
    return { user: response.data.userFound, token: response.data.token };
  } catch (error) {
    return thunkAPI.rejectWithValue(extractErrorMessage(error));
  }
});

// The API answers the same whether or not the email exists.
export const resendVerification = ({ email, lang }) =>
  axios.post(`${API_BASE_URL}/users/resend-verification`, { email, lang });

export const googleAuth = createAsyncThunk('auth/googleAuth', async (credential, thunkAPI) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/google`, { credential });
    if (response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.userFound));
      localStorage.setItem('token', response.data.token);
      return { user: response.data.userFound, token: response.data.token };
    }
    return thunkAPI.rejectWithValue('No token received');
  } catch (error) {
    return thunkAPI.rejectWithValue(extractErrorMessage(error));
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: { reset: (state) => { state.isLoading = false; state.isError = false; state.isSuccess = false; state.message = ''; state.pendingVerificationEmail = null; } },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.isLoading = true; state.isError = false; })
      .addCase(login.fulfilled, (state, action) => { state.isLoading = false; state.isSuccess = true; state.user = action.payload.user; state.token = action.payload.token; })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false; state.isError = true; state.user = null; state.token = null;
        state.message = action.payload?.message ?? action.payload;
        state.pendingVerificationEmail = action.payload?.unverifiedEmail ?? null;
      })
      .addCase(register.pending, (state) => { state.isLoading = true; state.isError = false; })
      .addCase(register.fulfilled, (state, action) => { state.isLoading = false; state.isSuccess = true; state.pendingVerificationEmail = action.payload.email; })
      .addCase(register.rejected, (state, action) => { state.isLoading = false; state.isError = true; state.message = action.payload; state.user = null; state.token = null; })
      .addCase(verifyEmail.pending, (state) => { state.isLoading = true; state.isError = false; })
      .addCase(verifyEmail.fulfilled, (state, action) => { state.isLoading = false; state.isSuccess = true; state.user = action.payload.user; state.token = action.payload.token; })
      .addCase(verifyEmail.rejected, (state, action) => { state.isLoading = false; state.isError = true; state.message = action.payload; })
      .addCase(googleAuth.pending, (state) => { state.isLoading = true; state.isError = false; })
      .addCase(googleAuth.fulfilled, (state, action) => { state.isLoading = false; state.isSuccess = true; state.user = action.payload.user; state.token = action.payload.token; })
      .addCase(googleAuth.rejected, (state, action) => { state.isLoading = false; state.isError = true; state.message = action.payload; })
      .addCase(logout.fulfilled, (state) => { state.user = null; state.token = null; state.isSuccess = false; });
  },
});

export const { reset } = authSlice.actions;
export default authSlice.reducer;